import { useState } from "react";
import { Bell, Check, Plus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useSavedTopics } from "@/hooks/useSavedTopics";
import SignupNudge from "@/components/SignupNudge";
import { toast } from "sonner";

interface TrackTopicInlineProps {
  query: string;
}

/**
 * Compact one-row variant of SaveTopicBanner.
 * Bell icon · short copy · Track button — all on a single line.
 * Sits between the results header and the NewsletterCapture report box.
 */
export default function TrackTopicInline({ query }: TrackTopicInlineProps) {
  const { user } = useAuth();
  const { savedQueries, saveTopic } = useSavedTopics();
  const [showNudge, setShowNudge] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const isAlreadySaved = savedQueries.has(query.toLowerCase().trim()) || justSaved;

  const handleSave = async () => {
    if (!user) {
      setShowNudge(true);
      return;
    }
    const success = await saveTopic(query);
    if (success) {
      setJustSaved(true);
      toast.success("Topic saved! We'll notify you when new episodes drop.");
    } else {
      toast.info("You're already tracking this topic.");
    }
  };

  return (
    <>
      <div className="retro-border bg-card px-3 py-2 flex items-center gap-2 animate-fade-in">
        <Bell className={`w-4 h-4 flex-shrink-0 ${isAlreadySaved ? "text-primary" : "text-primary"}`} />

        <p className="font-mono font-bold text-[11px] sm:text-xs text-foreground flex-1 min-w-0 truncate">
          {isAlreadySaved ? (
            <>
              <span className="hidden sm:inline">You're tracking this topic — we'll email you on new matches.</span>
              <span className="sm:hidden">Tracking this topic.</span>
            </>
          ) : (
            <>
              <span className="hidden sm:inline">Get notified when new episodes cover this topic.</span>
              <span className="sm:hidden">Get alerts for new episodes.</span>
            </>
          )}
        </p>

        <button
          onClick={handleSave}
          disabled={isAlreadySaved}
          className={`comic-play-btn relative overflow-hidden flex-shrink-0 flex items-center gap-1 font-mono font-bold text-[11px] px-2.5 py-1 border-2 rounded-md transition-all duration-150 transform ${
            isAlreadySaved
              ? "bg-primary/10 text-primary border-primary/30 cursor-default"
              : "bg-primary text-primary-foreground border-[hsl(24,80%,35%)] shadow-[3px_3px_0px_0px_hsl(var(--foreground))] hover:shadow-[1px_1px_0px_0px_hsl(var(--foreground))] hover:translate-x-[2px] hover:translate-y-[2px] hover:rotate-[-0.5deg] hover:scale-[1.03] active:scale-95"
          }`}
        >
          {isAlreadySaved ? (
            <>
              <Check className="w-3 h-3 relative z-10" />
              <span className="relative z-10">Tracking</span>
            </>
          ) : (
            <>
              <Plus className="w-3 h-3 relative z-10" />
              <span className="relative z-10 hidden sm:inline">Add to Tracked Topics</span>
              <span className="relative z-10 sm:hidden">Track</span>
            </>
          )}
        </button>
      </div>

      <SignupNudge open={showNudge} onClose={() => setShowNudge(false)} action="save" />
    </>
  );
}
